import { TrendingDown, TrendingUp, TriangleAlert as AlertTriangle, CircleCheck as CheckCircle2, Bell, ShieldCheck, ArrowRight, RefreshCw, Sparkles } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Link } from "@/components/common/Link";
interface CompSetRate {
  id: string;
  property: string;
  channel: string;
  roomType: string;
  compRate: string;
  yourRate: string;
  variance: string;
  isPositive: boolean;
  isNeutral?: boolean;
}
interface ParityAlert {
  id: string;
  message: string;
  severity: "High" | "Medium" | "Resolved";
  timeAgo: string;
}
const compSetRates: CompSetRate[] = [{
  id: "1",
  property: "The Grand Regal",
  channel: "Booking.com",
  roomType: "Deluxe King Room",
  compRate: "$195",
  yourRate: "$180",
  variance: "-7.7%",
  isPositive: false
}, {
  id: "2",
  property: "Harbor View Suites",
  channel: "Expedia",
  roomType: "Deluxe King Room",
  compRate: "$175",
  yourRate: "$180",
  variance: "+2.8%",
  isPositive: true
}, {
  id: "3",
  property: "Apex City Hotel",
  channel: "Agoda",
  roomType: "Executive Suite",
  compRate: "$260",
  yourRate: "$245",
  variance: "-5.8%",
  isPositive: false
}, {
  id: "4",
  property: "Azure Bay Resort",
  channel: "Direct Web",
  roomType: "Standard Queen",
  compRate: "$140",
  yourRate: "$140",
  variance: "0.0%",
  isPositive: false,
  isNeutral: true
}, {
  id: "5",
  property: "Marina Crest Hotel",
  channel: "MakeMyTrip",
  roomType: "Executive Suite",
  compRate: "$230",
  yourRate: "$245",
  variance: "+6.5%",
  isPositive: true
}];
const parityAlerts: ParityAlert[] = [{
  id: "a1",
  message: "Rate undercut detected on Booking.com",
  severity: "High",
  timeAgo: "12m ago"
}, {
  id: "a2",
  message: "Price drop alert on Expedia",
  severity: "Medium",
  timeAgo: "44m ago"
}, {
  id: "a3",
  message: "Closed room inventory open on Agoda",
  severity: "High",
  timeAgo: "2h ago"
}, {
  id: "a4",
  message: "Parity restored on Direct Channel",
  severity: "Resolved",
  timeAgo: "5h ago"
}];
export default function RateShoppingParityMonitoring() {
  return <section id="rateshoppingparitymonitoring" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-10">
          <Badge variant="outline" className="mb-3 px-3 py-1 text-xs font-semibold tracking-wide border-secondary/40 text-foreground bg-secondary/10">
            <Sparkles className="size-3 text-secondary" />
            Monitoring Intelligence
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground text-balance mb-4">
            Rate shopping and parity monitoring
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed max-w-2xl">
            Real-time comp-set price intelligence and OTA parity tracking to protect your margins and enforce rate integrity across every channel.
          </p>
        </div>

        {/* Live Status Bar */}
        <div className="bg-card border border-border rounded-xl p-4 sm:p-5 mb-8 shadow-xs flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3 text-center sm:text-left">
            <Badge variant="secondary" className="font-medium bg-secondary text-secondary-foreground">
              Sample Data for Illustration Purposes
            </Badge>
            <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
              <RefreshCw className="size-3.5 text-secondary shrink-0 animate-spin [animation-duration:8s]" />
              <span>Synced across 6 channels · 5 local comp-set properties tracked</span>
            </div>
          </div>
          <Button asChild size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 w-full sm:w-auto shrink-0">
            <Link to="#">
              Audit your rates
              <ArrowRight className="size-3.5 ml-1.5" />
            </Link>
          </Button>
        </div>

        {/* Quick Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
          <Card className="border-border shadow-xs bg-card">
            <CardHeader className="p-5 pb-2">
              <CardDescription className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                Comp-Set Median Rate
              </CardDescription>
              <CardTitle className="text-3xl font-bold font-serif text-foreground">
                $188
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5 pt-0 text-xs text-muted-foreground">
              Weighted median across 5 primary competitor sets
            </CardContent>
          </Card>

          <Card className="border-border shadow-xs bg-card">
            <CardHeader className="p-5 pb-2">
              <CardDescription className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                Your Property Rate
              </CardDescription>
              <CardTitle className="text-3xl font-bold font-serif text-foreground flex items-center justify-between">
                <span>$180</span>
                <span className="text-xs font-sans font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                  -$8 vs market avg
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5 pt-0 text-xs text-muted-foreground">
              Optimal dynamic positioning for current lead time
            </CardContent>
          </Card>

          <Card className="border-border shadow-xs bg-card">
            <CardHeader className="p-5 pb-2">
              <CardDescription className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                Parity Compliance Score
              </CardDescription>
              <CardTitle className="text-3xl font-bold font-serif text-foreground flex items-center justify-between">
                <span>94.2%</span>
                <span className="text-xs font-sans font-medium text-secondary bg-secondary/15 px-2 py-0.5 rounded border border-secondary/30">
                  Optimal health
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5 pt-0 text-xs text-muted-foreground">
              Direct web &amp; major OTA rate synchronization
            </CardContent>
          </Card>

          <Card className="border-border shadow-xs bg-card">
            <CardHeader className="p-5 pb-2">
              <CardDescription className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                Active Rate Alerts
              </CardDescription>
              <CardTitle className="text-3xl font-bold font-serif text-foreground flex items-center justify-between">
                <span>2 pending</span>
                <span className="size-2.5 rounded-full bg-destructive animate-pulse" />
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5 pt-0 text-xs text-muted-foreground">
              Actionable rule breaches requiring channel updates
            </CardContent>
          </Card>
        </div>

        {/* Dashboard Main Grid: Comp Table + Parity Alert Feed */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10">
          {/* Competitor Rate Comparison Table */}
          <div className="lg:col-span-8">
            <Card className="border-border shadow-xs bg-card h-full flex flex-col justify-between">
              <CardHeader className="p-5 sm:p-6 border-b border-border/60">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <CardTitle className="text-xl font-bold text-foreground font-serif">
                      Competitor Rate Comparison
                    </CardTitle>
                    <CardDescription className="text-xs text-muted-foreground mt-1">
                      Live rate differentials by channel and room inventory category
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className="w-fit text-xs font-normal">
                    Live Feed
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-0 flex-1">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/40 hover:bg-muted/40">
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-foreground">
                        Property (Channel)
                      </TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-foreground hidden sm:table-cell">
                        Room Type
                      </TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-foreground text-right">
                        Comp Rate
                      </TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-foreground text-right">
                        Your Rate
                      </TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-foreground text-right">
                        Variance
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {compSetRates.map((item, idx) => <TableRow key={item.id} data-index={idx} className="hover:bg-muted/30">
                        <TableCell className="font-medium text-foreground py-3.5">
                          <div className="flex flex-col">
                            <span>{item.property}</span>
                            <span className="text-xs text-muted-foreground font-normal sm:hidden">
                              {item.roomType} · {item.channel}
                            </span>
                            <span className="text-xs text-muted-foreground font-normal hidden sm:inline">
                              {item.channel}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground hidden sm:table-cell">
                          {item.roomType}
                        </TableCell>
                        <TableCell className="text-right font-medium text-foreground">
                          {item.compRate}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-foreground">
                          {item.yourRate}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded ${item.isNeutral ? "bg-muted text-muted-foreground" : item.isPositive ? "bg-destructive/10 text-destructive" : "bg-emerald-50 text-emerald-700"}`}>
                            {item.isPositive && <TrendingUp className="size-3" />}
                            {!item.isPositive && !item.isNeutral && <TrendingDown className="size-3" />}
                            {item.variance}
                          </span>
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          {/* Parity Alert Feed */}
          <div className="lg:col-span-4">
            <Card className="border-border shadow-xs bg-card h-full flex flex-col">
              <CardHeader className="p-5 sm:p-6 border-b border-border/60">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Bell className="size-4 text-secondary" />
                    <CardTitle className="text-xl font-bold text-foreground font-serif">
                      Parity Alert Feed
                    </CardTitle>
                  </div>
                  <Badge variant="outline" className="text-xs text-muted-foreground">
                    Auto-Check
                  </Badge>
                </div>
                <CardDescription className="text-xs text-muted-foreground mt-1">
                  Automated notifications for rate discrepancies
                </CardDescription>
              </CardHeader>
              <CardContent className="p-5 flex-1 flex flex-col justify-between gap-3">
                <div className="space-y-3">
                  {parityAlerts.map((alert, idx) => <div key={alert.id} data-index={idx} className="p-3.5 rounded-lg border border-border/70 bg-background/50 hover:bg-background transition-colors flex items-start justify-between gap-3">
                      <div className="flex items-start gap-2.5">
                        {alert.severity === "High" && <AlertTriangle className="size-4 text-destructive shrink-0 mt-0.5" />}
                        {alert.severity === "Medium" && <AlertTriangle className="size-4 text-amber-500 shrink-0 mt-0.5" />}
                        {alert.severity === "Resolved" && <CheckCircle2 className="size-4 text-emerald-600 shrink-0 mt-0.5" />}
                        <div>
                          <p className="text-xs sm:text-sm font-medium text-foreground leading-snug">
                            {alert.message}
                          </p>
                          <span className="text-[11px] text-muted-foreground block mt-1">
                            {alert.timeAgo}
                          </span>
                        </div>
                      </div>
                      <Badge variant={alert.severity === "High" ? "destructive" : alert.severity === "Resolved" ? "secondary" : "outline"} className={`text-[10px] uppercase font-bold shrink-0 ${alert.severity === "Resolved" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : alert.severity === "Medium" ? "bg-amber-50 text-amber-800 border-amber-200" : ""}`}>
                        {alert.severity}
                      </Badge>
                    </div>)}
                </div>

                <div className="pt-2 border-t border-border/50 text-[11px] text-muted-foreground flex items-center gap-1.5">
                  <ShieldCheck className="size-3.5 text-secondary shrink-0" />
                  <span>Continuous 15-minute OTA parity crawlers active</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Footer CTA Strip */}
        <div className="bg-primary text-primary-foreground rounded-xl p-6 sm:p-8 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-center md:text-left">
            <h3 className="font-serif text-xl sm:text-2xl font-bold text-primary-foreground mb-1">
              Want automated rate intelligence for your property?
            </h3>
            <p className="text-primary-foreground/80 text-sm max-w-xl">
              Get comprehensive comp-set parity monitoring, demand alerts, and dynamic pricing recommendations built for your hotel.
            </p>
          </div>
          <Button asChild size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 shrink-0 w-full sm:w-auto font-semibold">
            <Link to="#">
              Book a rate audit
              <ArrowRight className="size-4 ml-2" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}